import {
  Divider,
  Form,
  PrimaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'
import { FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import {
  type TermsMethodsKindEnum,
  TermsMethodsKinds,
} from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import messages from './messages'

export interface CreateTermOrMethodFormSchema {
  name: string
  description: string
}

export interface ICreateTermOrMethodFormProps {
  kind: TermsMethodsKindEnum
  onSubmit: (values: CreateTermOrMethodFormSchema) => void
  onCancel: () => void
  isSaving: boolean
}

// Kind-aware field label copy (sentence case — fixes the Angular lowercase
// "{value} {type} Name" quirk)
const KIND_LABEL_MESSAGES = {
  [TermsMethodsKinds.SHIPPING_TERMS]: messages.kindLabelShippingTerms,
  [TermsMethodsKinds.PAYMENT_TERMS]: messages.kindLabelPaymentTerms,
  [TermsMethodsKinds.SHIPPING_METHODS]: messages.kindLabelShippingMethods,
} as const

export const CreateTermOrMethodForm = ({
  kind,
  onSubmit,
  onCancel,
  isSaving,
}: ICreateTermOrMethodFormProps) => {
  const intl = useProcurifyIntl()

  const kindLabel = intl.formatMessage(KIND_LABEL_MESSAGES[kind])
  const nameLabel = intl.formatMessage(messages.nameLabel, { kindLabel })
  const descriptionLabel = intl.formatMessage(messages.descriptionLabel, {
    kindLabel,
  })

  const formik = useFormik<CreateTermOrMethodFormSchema>({
    initialValues: { name: '', description: '' },
    validationSchema: Yup.object({
      name: Yup.string().required(
        intl.formatMessage(messages.nameRequiredError, { kindLabel })
      ),
      description: Yup.string().required(
        intl.formatMessage(messages.descriptionRequiredError, { kindLabel })
      ),
    }),
    onSubmit: (values, { setSubmitting }) => {
      onSubmit(values)
      // The mutation's isSaving is the in-flight indicator; failures keep the
      // form open, so Formik must not latch isSubmitting
      setSubmitting(false)
    },
  })

  return (
    <FormikProvider value={formik}>
      <Form.Content noValidate autoComplete='off'>
        <Space>
          <Form.TextField
            id='name'
            name='name'
            label={nameLabel}
            placeholder={nameLabel}
            required
            autoFocus
          />
          <Form.TextField
            id='description'
            name='description'
            label={descriptionLabel}
            placeholder={descriptionLabel}
            required
            multiline
            minRows={3}
          />

          <Divider />
          <Space direction='horizontal' justify='end'>
            <TertiaryButton type='button' onClick={onCancel}>
              {intl.formatMessage(messages.cancelButton)}
            </TertiaryButton>
            <PrimaryButton type='submit' isLoading={isSaving}>
              {intl.formatMessage(messages.addButton)}
            </PrimaryButton>
          </Space>
        </Space>
      </Form.Content>
    </FormikProvider>
  )
}

CreateTermOrMethodForm.displayName = 'CreateTermOrMethodForm'
